class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    this.content = this.mainDetailsToggle.querySelector('summary').nextElementSibling;
    this.closingTimeout;

    this.mainDetailsToggle.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onContainerKeyUp.bind(this));

    this.openHandler = this.open.bind(this);
    this.closeWithClosingClassHandler = this.closeWithClosingClass.bind(this);

    this.closeButton = this.querySelector('.disclosure__close-button');
    if(this.closeButton) {
      this.onClickCloseButtonHandler = this.onClickCloseButton.bind(this);
      this.closeButton.addEventListener('click', this.onClickCloseButtonHandler);
    }
    
    if(!this.dataset.ignoreHover) {
      this.isAbleToUseMouse = matchMedia('(hover: hover)').matches;

      this.debouncedOnChange = debounce((event) => {
        this.isAbleToUseMouse = matchMedia('(hover: hover)').matches;
        this.initHoverDetails();
      }, 300);

      this.debouncedOnChangeHandler = this.debouncedOnChange.bind(this);
  
      window.addEventListener('resize', this.debouncedOnChangeHandler);
  
      this.initHoverDetails();
    }
    
    this.initClickSummary();
  }

  disconnectedCallback() {
    if(this.isAbleToUseMouse) {
      Shopify.removeEventListener(this.mainDetailsToggle, 'mouseenter', this.openHandler);
      Shopify.removeEventListener(this.mainDetailsToggle, 'mouseleave', this.closeWithClosingClassHandler);
    }

    if(this.closeButton) {
      this.closeButton.removeEventListener('click', this.onClickCloseButtonHandler);
    }

    if(this.debouncedOnChangeHandler) {
      window.removeEventListener('resize', this.debouncedOnChangeHandler);
    }
  }

  initHoverDetails () {
    if(this.isAbleToUseMouse) {
      Shopify.addListener(this.mainDetailsToggle, 'mouseenter', this.openHandler);
      Shopify.addListener(this.mainDetailsToggle, 'mouseleave', this.closeWithClosingClassHandler);
    } else {
      this.mainDetailsToggle.removeEventListener('mouseenter', this.openHandler);
      this.mainDetailsToggle.removeEventListener('mouseleave', this.closeWithClosingClassHandler);
    }
  }

  initClickSummary() {
    this.mainDetailsToggle.querySelector('summary').addEventListener('click', (e) => {
      e.preventDefault();
      if (this.mainDetailsToggle.hasAttribute('open')) {
        this.closeWithClosingClass();
      } else {
        this.open();
      }
    });
  }

  closeWithClosingClass() {
    if(this.classList.contains('ignore-close')) return;
    this.mainDetailsToggle.classList.add('closing');
    if(this.closingTimeout) {
      clearTimeout(this.closingTimeout);
    }
    this.closingTimeout = setTimeout(() => {
      this.close();
      this.mainDetailsToggle.classList.remove('closing');
    }, 300);
  }

  onContainerKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    this.closeWithClosingClass();
    this.mainDetailsToggle.querySelector('summary').focus();
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.closeWithClosingClass();
    })
  }

  onClickCloseButton() {
    this.closeWithClosingClass();
  }

  playAnimation() {
    if (!this.animations) this.animations = this.content.getAnimations();
    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.animations.forEach(animation => animation.play());
    } else {
      this.animations.forEach(animation => animation.cancel());
    }
  }

  open() {
    this.mainDetailsToggle.setAttribute('open', true);
    this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', true);
    this.playAnimation();
  }

  close() {
    this.mainDetailsToggle.removeAttribute('open');
    this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', false);
  }
}

customElements.define('details-disclosure', DetailsDisclosure);

let hoverSupport = matchMedia('(hover: hover)').matches;
window.addEventListener('resize', () => {
  hoverSupport = matchMedia('(hover: hover)').matches;
});

// evhouse v1.1: coordinated menu handoff, 2026-09-12.
class HeaderMenu extends DetailsDisclosure {
  static instances = new Set();
  static ownsScrollLock = false;

  constructor() {
    super();
    this.header = document.querySelector('.header');
    this.stickyHeader = this.closest('sticky-header');
    this.menuGroup = this.closest('nav') || this.stickyHeader || this.header;
    this.mainDetailsToggle.addEventListener('toggle', this.onToggle.bind(this));
    this.querySelectorAll(':scope > details > .header__menu-item--link').forEach(element => element.addEventListener('click', this.onClickSummary.bind(this)));
    this.onViewportChange = () => this.close();
    window.addEventListener('resize', this.onViewportChange);
    // Release our locks before an outside cart/search/dialog can take ownership.
    this.onOutsideInteraction = event => {
      if(this.mainDetailsToggle.hasAttribute('open') && !this.contains(event.target)) this.close();
    };
    document.addEventListener('pointerdown', this.onOutsideInteraction, true);
    document.addEventListener('focusin', this.onOutsideInteraction, true);
    HeaderMenu.instances.add(this);
  }

  onClickSummary(event) {
    // Preserve parent links for pointer clicks; keyboard activation only expands.
    if(event.detail > 0 && hoverSupport && !this.dataset.ignoreHover) {
      event.preventDefault();
      window.location = event.currentTarget.dataset.href;
    }
  }

  cancelPendingClose() {
    clearTimeout(this.hoverLeaveTimeout);
    clearTimeout(this.closingTimeout);
    clearTimeout(this.focusOutTimeout);
    this.mainDetailsToggle.classList.remove('closing');
  }

  closeOtherMenus() {
    HeaderMenu.instances.forEach(menu => {
      if(menu !== this && menu.menuGroup === this.menuGroup &&
         menu.mainDetailsToggle.hasAttribute('open')) menu.close();
    });
  }

  syncSharedState() {
    const openMenus = [...HeaderMenu.instances].filter(menu =>
      menu.isConnected !== false && menu.mainDetailsToggle.hasAttribute('open'));
    const headers = new Set([...HeaderMenu.instances].map(menu => menu.stickyHeader));
    if(this.stickyHeader) headers.add(this.stickyHeader);
    headers.forEach(header => {
      if(!header) return;
      const hasOpenMenu = openMenus.some(menu => menu.stickyHeader === header);
      header.setPreventOpen(hasOpenMenu);
      header.setPreventHide(hasOpenMenu);
    });
    const needsScrollLock = openMenus.some(menu => menu.content.classList.contains('outside-viewport'));
    if(needsScrollLock && !document.body.classList.contains('overflow-hidden')) {
      document.body.classList.add('overflow-hidden');
      HeaderMenu.ownsScrollLock = true;
    } else if(!needsScrollLock && HeaderMenu.ownsScrollLock) {
      document.body.classList.remove('overflow-hidden');
      HeaderMenu.ownsScrollLock = false;
    }
  }

  updatePanelLayout() {
    this.content.classList.remove('outside-viewport');
    this.content.style.removeProperty('--top-position');
    if(!this.mainDetailsToggle.hasAttribute('open') || !this.header) return;
    document.documentElement.style.setProperty('--header-bottom-position-desktop',
      `${Math.floor(this.header.getBoundingClientRect().bottom)}px`);
    const bounds = this.content.getBoundingClientRect();
    if(bounds.bottom > document.documentElement.clientHeight - 50) {
      this.content.classList.add('outside-viewport');
      this.content.style.setProperty('--top-position', `${bounds.top}px`);
    }
  }

  open() {
    const wasOpen = this.mainDetailsToggle.hasAttribute('open');
    this.cancelPendingClose();
    // Establish the new owner before closing the old one: no unlocked frame.
    if(!wasOpen) super.open();
    this.closeOtherMenus();
    this.updatePanelLayout();
    this.syncSharedState();
  }

  close() {
    this.cancelPendingClose();
    super.close();
    this.updatePanelLayout();
    this.syncSharedState();
  }

  closeWithClosingClass(event) {
    if(this.classList.contains('ignore-close') || !this.mainDetailsToggle.hasAttribute('open')) return;
    clearTimeout(this.hoverLeaveTimeout);
    if(event && event.type === 'mouseleave') {
      this.hoverLeaveTimeout = setTimeout(() => {
        // Do not dismiss keyboard content just because the pointer moved away.
        if(!this.contains(document.activeElement)) super.closeWithClosingClass();
      }, 220);
    } else {
      super.closeWithClosingClass();
    }
  }

  onFocusOut() {
    clearTimeout(this.focusOutTimeout);
    this.focusOutTimeout = setTimeout(() => {
      if(!this.contains(document.activeElement)) this.closeWithClosingClass();
    }, 0);
  }

  onContainerKeyUp(event) {
    if(event.code.toUpperCase() !== 'ESCAPE') return;
    this.close();
    this.mainDetailsToggle.querySelector('summary').focus();
  }

  disconnectedCallback() {
    this.close();
    HeaderMenu.instances.delete(this);
    this.syncSharedState();
    window.removeEventListener('resize', this.onViewportChange);
    document.removeEventListener('pointerdown', this.onOutsideInteraction, true);
    document.removeEventListener('focusin', this.onOutsideInteraction, true);
    super.disconnectedCallback();
  }

  onToggle() {
    // Toggle events may arrive after another menu has already taken over.
    if(this.mainDetailsToggle.hasAttribute('open')) this.closeOtherMenus();
    this.updatePanelLayout();
    this.syncSharedState();
  }
}

customElements.define('header-menu', HeaderMenu);
